// ABOUTME: Reports list page showing all user reports.
// ABOUTME: Clean, minimal design with proper delete confirmation.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, FileText, Search, RefreshCw, Trash2, Globe, Lock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { apiClient } from '@/services/api';
import type { ReportListItem } from '@/types/api';
import { useApp } from '@/context/AppContext';
import { useTour } from '@/context/TourContext';
import { UpgradeBanner } from '@/components/billing/UpgradeBanner';
import { billingApi } from '@/services/api/billingClient';

export function ReportsPage() {
  const navigate = useNavigate();
  const { currentWorkspaceId, isFreeTier } = useApp();
  const { isRunning: isTourRunning, currentAction, onNewReportClicked } = useTour();
  const [searchQuery, setSearchQuery] = useState('');
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Proactive gating state for freemium users
  const [showCapPrompt, setShowCapPrompt] = useState(false);
  const [capInfo, setCapInfo] = useState<{ used: number; limit: number } | null>(null);

  // Fetch reports from backend
  const fetchReports = useCallback(async () => {
    if (!currentWorkspaceId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.reports.list({
        workspace_id: currentWorkspaceId,
        limit: 50
      });
      setReports(response.reports);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      setError('Failed to load reports. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentWorkspaceId]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Delete report handlers
  const handleDeleteClick = (e: React.MouseEvent, report: { id: string; title: string }) => {
    e.stopPropagation();
    setDeleteTarget(report);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    try {
      await apiClient.reports.delete(deleteTarget.id);
      setReports((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete report:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Filter and sort reports by last edited time (most recent first)
  const filteredReports = reports
    .filter((report) => report.title.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      const aTime = a.last_edited_at || a.updated_at;
      const bTime = b.last_edited_at || b.updated_at;
      return new Date(bTime + 'Z').getTime() - new Date(aTime + 'Z').getTime();
    });

  // Format relative time - dateString is UTC from API
  const formatRelativeTime = (dateString: string) => {
    // Check if it already has timezone info (Z, +XX:XX, or -XX:XX after the T)
    const hasTz = dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString);
    const normalized = hasTz ? dateString : dateString + 'Z';
    const date = new Date(normalized);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <UpgradeBanner pageKey="reports" message="Generate up to 10 reports per month with Pro" />

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="heading-page">
              Reports
            </h1>
            <p className="text-sm text-surface-400 mt-1">
              {reports.length} report{reports.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchReports}
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              onClick={async () => {
                // Proactive gating: check usage before creating for freemium users
                if (isFreeTier) {
                  try {
                    const usage = await billingApi.getUsage();
                    const reportUsage = usage.usage.reports_generated;
                    if (reportUsage.used >= reportUsage.limit) {
                      setCapInfo({ used: reportUsage.used, limit: reportUsage.limit });
                      setShowCapPrompt(true);
                      return;
                    }
                  } catch (err) {
                    // If usage check fails, allow creation to proceed
                    console.error('[ReportsPage] Failed to check usage:', err);
                  }
                }

                // Notify tour if it's waiting for this action
                if (isTourRunning && currentAction === 'click-new-report') {
                  onNewReportClicked();
                }
                navigate('/reports/new');
              }}
              leftIcon={<Plus className="w-4 h-4" />}
              data-tour="new-report"
            >
              New
            </Button>
          </div>
        </div>

        {/* Report Cap Prompt — shown when freemium user is at report generation limit */}
        {showCapPrompt && capInfo && (
          <div
            data-testid="report-cap-prompt"
            className="mb-6 p-4 card border border-primary-100 bg-primary-50 rounded-sm"
          >
            <h3 className="text-sm font-medium text-surface-800 mb-1">
              Report limit reached
            </h3>
            <p className="text-sm text-surface-600 mb-3">
              Your Free plan includes {capInfo.limit} report{capInfo.limit !== 1 ? 's' : ''} per month. Upgrade to Pro for more.
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => navigate('/upgrade')}
              >
                Upgrade
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCapPrompt(false)}
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <Input
            placeholder="Search reports..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>

        {/* Loading State */}
        {isLoading && reports.length === 0 && (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-6 h-6 text-primary-500 animate-spin" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-sm text-error mb-4">{error}</p>
            <Button variant="secondary" size="sm" onClick={fetchReports}>
              Try Again
            </Button>
          </div>
        )}

        {/* Reports List */}
        {!isLoading && !error && (
          <div className="space-y-2">
            {filteredReports.length > 0 ? (
              filteredReports.map((report, index) => (
                <motion.div
                  key={report.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => navigate(`/reports/${report.id}`)}
                  className="group flex items-center gap-4 px-4 py-3.5 card-outlined hover:bg-surface-50 cursor-pointer transition-all duration-200"
                >
                  <div className="w-9 h-9 rounded-sm bg-accent-50 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-accent-600" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium text-surface-800 truncate">
                        {report.title}
                      </h3>
                      {report.status === 'published' && (
                        <Globe className="w-3.5 h-3.5 text-success flex-shrink-0" />
                      )}
                      {report.status === 'draft' && (
                        <Lock className="w-3.5 h-3.5 text-surface-400 flex-shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-surface-400">
                        {report.section_count} section{report.section_count !== 1 ? 's' : ''}
                      </span>
                      <span className="text-surface-300">·</span>
                      <span className="text-xs text-surface-400">
                        {report.last_edited_at
                          ? `Edited ${formatRelativeTime(report.last_edited_at)}`
                          : formatRelativeTime(report.updated_at)}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={(e) => handleDeleteClick(e, report)}
                    className="p-2 rounded-sm opacity-0 group-hover:opacity-100 hover:bg-error-light transition-all"
                    aria-label="Delete report"
                  >
                    <Trash2 className="w-4 h-4 text-surface-400 hover:text-error transition-colors" />
                  </button>
                </motion.div>
              ))
            ) : reports.length === 0 ? (
              <div className="empty-state">
                <div className="w-14 h-14 rounded-sm bg-accent-50 flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-7 h-7 text-accent-500" />
                </div>
                <h3 className="empty-state-title">
                  No reports yet
                </h3>
                <p className="empty-state-description">
                  Create reports from your investigations to share insights with your team.
                </p>
                <Button onClick={() => navigate('/reports/new')} size="sm">
                  Create Report
                </Button>
              </div>
            ) : (
              <div className="text-center py-12">
                <Search className="w-6 h-6 text-surface-400 mx-auto mb-3" />
                <p className="text-sm text-surface-400">
                  No reports match "{searchQuery}"
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Report"
        message={`Are you sure you want to delete "${deleteTarget?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        isLoading={isDeleting}
      />
    </div>
  );
}
